import { isLimitThresholdColumn } from '@monorepo/logs/src/components/Views/ViewResultsTable/resultsMapper';
import {
  ColumnSet,
  ColumnType,
  LimitColumnSet,
  QueryColumn,
} from 'mapistry-shared';
import { DeepPartial } from 'utility-types';
import { FormValues } from './types';

const COLUMN_TYPES_ALLOWED_TO_GROUP_BY = [
  ColumnType.BOOLEAN,
  ColumnType.FOREIGN_ID,
  ColumnType.TEXT,
];

function isColumnWithUserFriendlyValue(column: QueryColumn) {
  return column.columnType !== ColumnType.FOREIGN_ID && !column.isSystemColumn;
}

// If an id column doesn't have a columnSet, there are no dependant columns to group by and
// to show in View results table. If we allow grouping by such columns, a user won't know how the data was
// grouped-by by just looking at resulting rows.
// For example, we want to filter out columns that originated from resource field dataset properties,
// because for dataset Reports, if a user wants to group by resource itself they should do it by record property.
function isIdOrSystemColumnWithoutColumnSet(
  column: QueryColumn,
  columnSets: ColumnSet[],
  columnsByColumnName: { [columnName: string]: QueryColumn },
) {
  if (isColumnWithUserFriendlyValue(column)) {
    return false;
  }
  const columnSet = columnSets.find(
    (cs) => cs.parentColumn === column.columnName,
  );
  if (!columnSet) return true;
  // If all child columns don't have a user-friendly value, there is no point to group by a parent,
  // users won't see in the View table what they are grouping by
  return columnSet.dependentColumns.every((columnName) => {
    const childColumn = columnsByColumnName[columnName];
    return !childColumn || !isColumnWithUserFriendlyValue(childColumn);
  });
}

export function getAllowedGroupByColumns(
  availableColumns: QueryColumn[],
  columnSets: ColumnSet[],
  limitColumnSets: LimitColumnSet[],
  currentFormValues: DeepPartial<FormValues>,
  fieldIndex?: number,
): QueryColumn[] {
  const alreadySelectedGroupBys = (currentFormValues?.groupBy || []).filter(
    (columnName): columnName is string => !!columnName,
  );
  // If a user already aggregated by a column, don't allow to group-by its parent, because we will group by all children under the hood.
  // Also even though, we don't allow grouping by numeric columns, and for most operations a user can aggregate only by numbers,
  // there is still "count distinct" operation for which a user can select any column type.
  // So it's possible for a user to select exactly the same column in aggregation and group-by if not restricted.
  // It doesn't make sense to aggregate and group by the same column, so removing them from selectable options.
  const alreadySelectedColumnsForAggregation = (
    currentFormValues?.columns || []
  ).flatMap(({ columnName }) => {
    if (!columnName) return [];
    const columnSet = columnSets.find((cs) =>
      cs.dependentColumns.includes(columnName),
    );
    const ancestors = columnSet
      ? [columnSet.parentColumn, ...(columnSet.dependentParentColumns || [])]
      : [];
    return [columnName, ...ancestors];
  });

  const disallowedColumnOptions = alreadySelectedGroupBys.reduce(
    (
      excludedColumnNames: string[],
      selectedGroupByColumnName: string,
      idx: number,
    ) => {
      // if we're looking at selected column for the dropdown we're getting the options for, don't remove column options
      //  that that selection disqualifies
      // Examples:
      //  - if a certain group by column is set to column "Type", the dropdown for that should still show "Type" as an option
      //  - if it is set to a resource, the dropdown should allow switching from that resource to one of its dependent fields
      //  - if it is set to a resource property, the dropdown should allow switching to the resource itself (UNLESS another
      //    selected group by column also is set to one of that resource's properties)
      if (idx === fieldIndex) {
        return excludedColumnNames;
      }

      // not allowed to group by the same column multiple times
      const toExclude: string[] = [selectedGroupByColumnName];

      // for each column set, can only choose the parent column OR a subset of the dependent columns
      //  (if you are grouping by resource, for example, it is redundant to group by a specific property on that resource)
      columnSets.forEach((cs) => {
        if (cs.parentColumn === selectedGroupByColumnName) {
          toExclude.push(...cs.dependentColumns);
          toExclude.push(...(cs.dependentParentColumns || []));
        }
        if (cs.dependentColumns.includes(selectedGroupByColumnName)) {
          toExclude.push(cs.parentColumn);
        }
        if (
          (cs.dependentParentColumns || []).includes(selectedGroupByColumnName)
        ) {
          toExclude.push(cs.parentColumn);
        }
      });

      return [...excludedColumnNames, ...toExclude];
    },
    [],
  );

  const columnsByColumnName = availableColumns.reduce(
    (map, c) => ({
      ...map,
      [c.columnName]: c,
    }),
    {} as { [columnName: string]: QueryColumn },
  );

  const groupByOptions = availableColumns.filter(
    (c) =>
      COLUMN_TYPES_ALLOWED_TO_GROUP_BY.includes(c.columnType) &&
      !disallowedColumnOptions.includes(c.columnName) &&
      !isIdOrSystemColumnWithoutColumnSet(c, columnSets, columnsByColumnName) &&
      !isLimitThresholdColumn(c.columnName, limitColumnSets) &&
      !alreadySelectedColumnsForAggregation.includes(c.columnName),
  );
  return groupByOptions;
}
